import React, { useContext } from 'react';
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';

import { sortBy, uniq } from 'lodash';

import { Flex, Layout, Tabs, Tag } from 'antd';
import AddButton from 'components/action/AddButton';
import StakeEditionModal from 'components/Modal/StakeEditionModal';

import SubHeaderPortal from 'layout/header/SubHeaderPortal';
import { ConfirmModal } from 'components/Modal/ConfirmModal';

import {
	getItemAlteredByScenario
} from 'utils/rules';

import UIContext from 'components/Utils/UIContext';
import { SideMenu } from 'layout/SideMenu';
import { ColumnsDisplayTab } from 'components/tabs/ColumnsDisplayTab';

import { useStakeController } from 'hooks/useStakeController';
import dayjs from 'dayjs';
import { MaskableComponent } from 'components/Tutorial/MaskableComponent';
import Tutorial from 'components/Tutorial/Tutorial';
import { StakesList } from './StakesList';
import { StakesRadarChart } from './StakesRadarChart';
import Nav from '../Scenarios/Nav';
import { useOnboardingStep } from 'components/Onboarding/OnboardingReviewBanner';
import CommentsTab from '../Navigator/Tabs/CommentsTab';
import { StakeGeneralTab } from './Tabs/StakeGeneralTab';
import BotTab from 'components/tabs/BotTab';
import DisabledTab from 'components/tabs/DisabledTab';
import { FiltersTab } from './Tabs/FiltersTab';
import { StakeModel } from 'model/StakeModel';
import { RiskModel } from 'model/RiskModel';
import { useRiskController } from 'hooks/useRiskController';
import { useStakeholderController } from 'hooks/useStakeholderController';
import { StakeholderImpactsTab } from 'components/tabs/StakeholderImpactsTab';
import { FishboneLinksTab } from 'components/tabs/FishboneLinksTab';
import { ItemTagsTab } from 'components/tabs/ItemTagsTab';
import { ExportTab } from './Tabs/ExportTab';

import './Stakes.sass';

const stakesLimit = 25;
const selectableElementTypes = ['Stake']

type FilterObject = {
	name: string,
	riskFilter: string,
	opportunitiesFilter: string,
	tagFilter: string[],
}


const filterStakes = (stakes: StakeModel[], filters: FilterObject, risks: RiskModel[]) => {

	let limitedStakes = stakes.map((r, index) => ({ ...r, index }))

	if (filters && filters.riskFilter) {
		limitedStakes = limitedStakes.filter(r => {
			return risks.some(risk => risk.impactedStakes.some(ri => ri.stakeId == r.id && ri.riskId == filters.riskFilter))
		})
	}

	if (filters && filters.opportunitiesFilter) {
		limitedStakes = limitedStakes.filter(r => {
			return risks.some(risk => risk.impactedStakes.some(ri => ri.stakeId == r.id && ri.riskId == filters.opportunitiesFilter))
		})
	}

	if (filters && filters.name) {
		limitedStakes = limitedStakes.filter(r => {
			return r.name.toLowerCase().includes(filters.name.toLowerCase())
		})
	}

	if (filters && filters.tagFilter && filters.tagFilter.length > 0) {
		limitedStakes = limitedStakes.filter(r =>
			(r.taggings || []).some((t: any) => filters.tagFilter.includes(t.tagId))
		)
	}

	return limitedStakes
}

export const Stakes = (props: any) => {
	const [editedItem, setEditedItem] = React.useState<any | null>(null);
	const params: any = useParams();

	const { createStake, updateStake, updateStakeTaggings, deleteStake } = useStakeController();
	const { createStakeImpact, deleteStakeImpact, updateStakeImpactStrength } = useRiskController();
	// Stakeholder -> Stake impact controller (different from useRiskController's
	// createStakeImpact which is RISK -> Stake). Rename via destructuring.
	const {
		createStakeImpact: createStakeholderStakeImpact,
		deleteStakeImpact: deleteStakeholderStakeImpact,
		updateStakeImpactStrength: updateStakeholderStakeImpactStrength
	} = useStakeholderController();

	const { t } = useTranslation();
	const onboarding = useOnboardingStep('stakes');

	const { stakes = [] } = props;

	let alteredStakes = sortBy(
		stakes.map((c, index) => ({
			...getItemAlteredByScenario(c, props.scenario),
			index: index,
			key: c.id || `stake-${index}` // Ajout d'une clé unique pour chaque élément
		})
		).filter(c => c.enabled !== false),
		'name'
	);

	const [riskFilter, setRiskFilter] = React.useState<any | null>(location?.state?.riskFilter || null);
	const [opportunitiesFilter, setOpportunitiesFilter] = React.useState<any | null>(location?.state?.opportunitiesFilter || null);
	const [tagFilter, setTagFilter] = React.useState<string[]>([]);

	// Onglet actif de la page (liste « enjeux » par défaut, « summary » = viz radar).
	// Piloté par un état local, pas de route dédiée.
	const [activeTab, setActiveTab] = React.useState<string>('stakes');

	// Largeur du chart : le radar est paramétrique sur sa largeur. Callback ref +
	// ResizeObserver pour survivre au montage/démontage sur changement d'onglet
	// (même approche que la page Risks). Cappée à ~900px.
	const [vizWidth, setVizWidth] = React.useState<number>(900);
	const vizWidthRef = React.useRef<number>(900);
	const vizResizeObserverRef = React.useRef<ResizeObserver | null>(null);
	const vizContainerRef = React.useCallback((node: HTMLDivElement | null) => {
		if (vizResizeObserverRef.current) {
			vizResizeObserverRef.current.disconnect();
			vizResizeObserverRef.current = null;
		}
		if (node && typeof ResizeObserver !== 'undefined') {
			const ro = new ResizeObserver((entries) => {
				const w = entries[0]?.contentRect?.width;
				if (!w) return;
				const next = Math.min(Math.round(w), 900);
				// Garde anti-boucle : ignore les oscillations sub-pixel (sinon
				// re-render perpétuel, cf. fix page /risks).
				if (Math.abs(next - vizWidthRef.current) < 2) return;
				vizWidthRef.current = next;
				setVizWidth(next);
			});
			ro.observe(node);
			vizResizeObserverRef.current = ro;
		}
	}, []);

	// Hauteur d'ecran disponible : le radar doit aussi se borner verticalement
	// (sinon, sur un viewport court, le bas du graphe -- l'axe du bas -- est
	// tronque par la zone visible). On suit innerHeight et on en deduit la taille
	// max du radar cote hauteur.
	const [availH, setAvailH] = React.useState<number>(
		typeof window !== 'undefined' ? window.innerHeight : 900
	);
	React.useEffect(() => {
		if (typeof window === 'undefined') return;
		const onResize = () => setAvailH(window.innerHeight);
		window.addEventListener('resize', onResize);
		return () => window.removeEventListener('resize', onResize);
	}, []);

	// Cote du radar : il est carre et tient dans la plus petite des deux
	// contraintes -- largeur du conteneur (vizWidth, cappee 900) et hauteur
	// d'ecran disponible moins le chrome (tabs + nav scenario + marges ~ 280px).
	// Ainsi il se reduit avec l'ecran et n'est jamais tronque en bas.
	const radarSide = Math.max(320, Math.min(vizWidth, availH - 280));

	const allTags = sortBy(props.tags || [], 'name');
	const usedTags = uniq((alteredStakes as any[]).flatMap(s => (s.taggings || []).map((t: any) => t.tagId)));

	const getAggregatedFilters = (): FilterObject => {
		return {
			name: props.searchTerm,
			riskFilter: props.displayRisks && riskFilter ? riskFilter : null,
			opportunitiesFilter: props.displayRisks && opportunitiesFilter ? opportunitiesFilter : null,
			tagFilter,
		}
	}

	const filteredStakes = filterStakes(alteredStakes, getAggregatedFilters(), props.risks)

	const selection = props.selection?.__typename === "Stake" ? stakes.find((r: any) => r.id === props.selection?.id) : null;


	// Create
	const handleCreateStake = async () => {
		props.pushModal(
			<StakeEditionModal
				onHide={() => {
					props.popModal();
				}}
				onSave={async (values: any) => {
					let variables = {
						projectId: params.id,
						name: values.name,
						weight: values.weight || 1,
						trend: '50',
						description: values.description || '',
						reference: values.reference || '',
						evaluatedAt: values.evaluatedAt || ''
					};

					let result = await createStake(params.id, variables, {}, props.scenario, props.scenarios || []);

					props.popModal();
				}}
				id={false}
				values={{
					name: '',
					weight: 1,
					description: '',
					reference: '',
					evaluatedAt: null,
				}}
			></StakeEditionModal>
		);
	};


	// Update
	const handleUpdateStake = async (id: string, values: Object) => {
		updateStake(id, stakes || [], props.scenario, values);
	};

	const askDeleteConfirmation = async (id?: string) => {
		const confirmProps = {
			title: t('models.stake.messages.delete_confirmation'),
			message: t('models.stake.messages.delete_confirmation_explanation'),
			onConfirm: () => deleteStake(params.id, id),
			onHide: props.popModal,
			t
		};

		props.pushModal(
			<ConfirmModal {...confirmProps} />
		);
	};

	const uiContext = useContext(UIContext);

	return (
		<Layout className="Stakes" data-testid="stakes-page">
			<SubHeaderPortal>
				{!onboarding.active && (
					<Tutorial
						type="stakes"
						context={{ ...props }}
						executeAction={props.executeAction}
						projectId={params.id}
					/>
				)}
			</SubHeaderPortal>

			<Layout.Content className="Body pt-1">
				<MaskableComponent masked={uiContext.maskedZones['main']}>

					<Tabs
						type="card"
						activeKey={activeTab}
						onChange={setActiveTab}
						items={[
							{
								key: 'stakes',
								label: t('pages.stakes.title'),
								children: (
									<div className="content pb-3 d-flex flex-column align-items-center">

										<div className="pr-1 w-100">
											<StakesList
												stakes={filteredStakes}
												scenarios={props.scenarios}
												scenario={props.scenario}
												onUpdateStake={handleUpdateStake}
												onEditStake={setEditedItem}
												selection={selection}
												deselect={props.deselect}
												select={props.select}
												readOnly={!uiContext.canEdit}
												showDescription={uiContext.preferences?.pages?.stakes?.description !== false}
											/>

										</div>

										<Flex justify="center" align="center">
											{uiContext.canEdit && filteredStakes?.length < stakesLimit && (
												<AddButton
													id="add-stake"
													data-testid="add-stake-button"
													onClick={handleCreateStake}
												/>
											)}
										</Flex>

									</div>
								)
							},
							{
								key: 'summary',
								label: t('pages.risks.visualization_tab'),
								children: (
									<div
										ref={vizContainerRef}
										style={{ width: '100%' }}
										data-testid="stakes-visualization"
									>
										<div style={{ maxWidth: vizWidth, margin: '0 auto', width: '100%' }}>
											<Nav
												className="mb-2 w-100"
												fullWidth
												onGoToPreviousScenario={props.onGoToPreviousScenario}
												onGoToNextScenario={props.onGoToNextScenario}
											>
												<div
													className="text-center"
													style={{
														height: '3.4rem',
														display: 'flex',
														alignItems: 'center',
														justifyContent: 'center'
													}}
												>
													<strong style={{ fontSize: '1.1rem' }}>
														{props.scenario?.name || t('models.scenario.baseline')}
													</strong>
												</div>
											</Nav>
										</div>
										<Flex justify="center">
											<StakesRadarChart
												stakes={stakes}
												scenario={props.scenario}
												scenarios={props.scenarios}
												width={radarSide}
												height={radarSide}
												select={props.select}
												onSelectScenario={props.onGoToScenario}
											/>
										</Flex>
									</div>
								)
							}
						]}
					/>
				</MaskableComponent>
			</Layout.Content>

			<SideMenu defaultTab={props.selection && selection?.__typename && selectableElementTypes.includes(selection?.__typename) ? "general" : "filters"} tabs={(() => {
				// Calcul du nombre d'éléments désactivés
				const disabledCount = stakes.filter(i => getItemAlteredByScenario(i, props.scenario).enabled === false).length;

				return [
					(selection?.__typename && selectableElementTypes.includes(selection?.__typename)) && {
						id: 'general',
						title: t('pages.stakes.tabs.general.title'),
					},
					!selection && {
						id: 'filters',
						title: t('pages.stakes.tabs.filters.title')
					},
					!selection && {
						id: 'display',
						title: t('pages.risks.tabs.options.title')
					},
					selection?.__typename === 'Stake' && props.features?.fishbone && {
						id: 'fishbone',
						title: t('pages.fishbone.title')
					},
					selection?.__typename === 'Stake' && {
						id: 'stakeholders',
						title: t('models.item.name_plural')
					},
					selection?.__typename === 'Stake' && {
						id: 'tags',
						title: t('pages.navigator.tabs.tags.title')
					},
					selection?.__typename && selectableElementTypes.includes(selection?.__typename) && {
						id: 'comments',
						title: t('pages.stakes.tabs.comments.title')
					},
					!selection && {
						id: 'deactivated',
						title: `${t('pages.navigator.tabs.disabled.title')}${disabledCount > 0 ? ` (${disabledCount})` : ''}`
					},
					!selection && {
						id: 'bot',
						title: t('pages.stakes.tabs.bot.title')
					},
					!selection && {
						id: 'export',
						title: t('pages.navigator.tabs.export.title')
					},
				];
			})()}>
				{function (selectedTab, setSelectedTab: (t: string) => void) {
					return <>{selectedTab == 'comments' && (
						<CommentsTab
							projectId={params.id}
							key={props.selection.id}
							type={props.selection?.__typename}
							selection={props.selection}
							pushModal={props.pushModal}
							popModal={props.popModal}
							scenario={props.scenario}
						></CommentsTab>
					)}

						{selectedTab == 'general' && selection?.__typename === "Stake" && (<>
							<StakeGeneralTab
								selection={selection}
								risks={props.risks}
								indicators={props.indicators}
								fishboneBranchs={props.fishboneBranchs}
								onEdit={() => {
									setEditedItem(selection)
								}}
								onDisable={async () => {
									await updateStake(selection?.id, stakes, props.scenario, { enabled: false });
									props.deselect();
								}}
								onDelete={askDeleteConfirmation}

								scenario={props.scenario}

								createStakeImpact={createStakeImpact}
								deleteStakeImpact={deleteStakeImpact}
								updateImpactStrength={updateStakeImpactStrength}

								onClose={() => {
									props.deselect()
								}}
							></StakeGeneralTab>
							<div className="px-1 mt-1" onClick={() => setSelectedTab('tags')} style={{ cursor: 'pointer' }}>
								{(selection?.taggings?.length > 0) ? (
									(selection.taggings as any[]).map(tagging => {
										const tag = allTags.find(t => t.id === tagging.tagId);
										return tag ? (
											<Tag.CheckableTag key={tag.id} checked className="noselection">
												{tag.name}
											</Tag.CheckableTag>
										) : null;
									})
								) : (
									<span style={{ color: 'var(--ant-color-text-tertiary)', fontSize: '0.85rem' }}>Tags →</span>
								)}
							</div>
						</>)}

						{selectedTab === 'stakeholders' && selection?.__typename === 'Stake' && (
							<StakeholderImpactsTab
								selection={selection}
								stakeholders={props.stakeholders || []}
								impactedFieldName="impactedStakes"
								targetIdFieldName="stakeId"
								entityTypeLabel={t('models.stake.name')}
								createImpact={async (stakeholderId, stakeId) => {
									await createStakeholderStakeImpact(stakeholderId, stakeId);
									props.select({ ...selection });
								}}
								updateImpactStrength={async (impact, strength) => {
									await updateStakeholderStakeImpactStrength(impact, strength);
									props.select({ ...selection });
								}}
								deleteImpact={async (id, stakeholderId) => {
									await deleteStakeholderStakeImpact(id, stakeholderId);
									props.select({ ...selection });
								}}
								onClose={() => props.deselect()}
							/>
						)}

						{selectedTab === 'fishbone' && selection?.__typename === 'Stake' && (
							<FishboneLinksTab
								selection={selection}
								targetType="stake"
								fishboneBranchs={props.fishboneBranchs}
								scenario={props.scenario}
								select={props.select}
								onClose={() => props.deselect()}
							/>
						)}

						{selectedTab == 'tags' && selection?.__typename === 'Stake' && (
							<ItemTagsTab
								allTags={props.tags || []}
								item={selection}
								onClose={() => props.deselect()}
								onUpdateTags={async (item, tagIds) => {
									await updateStakeTaggings(item, tagIds);
									const kept = (item.taggings || []).filter((t: any) => tagIds.includes(t.tagId));
									const added = tagIds.filter((id: string) => !(item.taggings || []).find((t: any) => t.tagId === id)).map((id: string) => ({ tagId: id, objectId: item.id }));
									props.select({ ...item, taggings: [...kept, ...added] });
								}}
								onCreateTag={props.onCreateTag}
								areTagsVisible={props.preferences?.pages?.navigator?.tags ?? true}
								setPreferences={props.setPreferences}
								scenario={props.scenario}
							/>
						)}

						{selectedTab == 'filters' && (
							<FiltersTab
								setSearchTerm={props.setSearchTerm}
								searchTerm={props.searchTerm}
								selection={selection}
								deselect={props.deselect}
								select={props.select}
								onEdit={() => {
									setEditedItem(selection)
								}}
								risks={props.risks}
								riskFilter={riskFilter}
								opportunitiesFilter={opportunitiesFilter}
								setRiskFilter={setRiskFilter}
								setOpportunitiesFilter={setOpportunitiesFilter}
								displayRisks={props.displayRisks}
								tags={allTags}
								usedTags={usedTags}
								tagFilter={tagFilter}
								setTagFilter={setTagFilter}
							></FiltersTab>
						)}

						{selectedTab == 'display' && (
							<ColumnsDisplayTab
								displayColumns={activeTab !== 'summary'}
								preferences={uiContext.preferences?.pages?.stakes}
								setPreferences={(key, value) =>
									uiContext.setPreferences('pages', 'stakes.' + key, value)
								}
							/>
						)}

						{selectedTab == 'bot' && (
							<BotTab
								projectId={params.id}
								messages={props.botThread?.messages || []}
								onResetThread={props.resetBotThread}
								onSendMessage={(message, callback) => props.addBotMessage(params.id, props.scenario?.id || null, "stakes", message, callback)}

							></BotTab>
						)}

						{selectedTab == 'deactivated' && (
							<DisabledTab
								scenario={props.scenario}
								stakes={props.stakes}
								select={props.select}
								onEnableItem={async (item) => {
									if (item.__typename === 'Stake') {
										await updateStake(item.id, stakes, props.scenario, { enabled: true });
									}
								}}
								onDeleteItem={(item) => {
									if (item.__typename === 'Stake') {
										askDeleteConfirmation(item.id);
									}
								}}
							/>
						)}

						{selectedTab == 'export' && (
							<ExportTab stakes={filteredStakes} />
						)}
					</>
				}}
			</SideMenu>

			{editedItem && (() => {
				// Appliquer les valeurs du scénario si un scénario est actif
				const alteredEditedItem = getItemAlteredByScenario(editedItem, props.scenario);
				return (
					<StakeEditionModal
						onHide={() => {
							setEditedItem(null);
						}}
						onDelete={() => {
							askDeleteConfirmation(editedItem.id);
							setEditedItem(null);
						}}
						onSave={async (values: any) => {
							await handleUpdateStake(editedItem.id, values);
							if (values.taggings !== undefined)
								await updateStakeTaggings(editedItem, values.taggings);
							setEditedItem(null);
						}}
						id={editedItem?.id}
						tags={props.tags || []}
						values={{
							// Champs baseline (non modifiables par scénario)
							name: editedItem?.name,
							description: editedItem?.description || '',
							reference: editedItem?.reference,
							evaluatedAt: editedItem?.evaluatedAt ? dayjs(editedItem?.evaluatedAt) : null,
							taggings: editedItem?.taggings?.map((t: any) => t.tagId) || [],
							// Champs modifiables par scénario - utiliser les valeurs altérées
							weight: alteredEditedItem?.weight || 1,
						}}
					/>
				);
			})()}
		</Layout>
	);
};
